/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction
} from '@reduxjs/toolkit'
import * as Services from '../../services/endpoints'
import * as Types from '../../types'
import { ResultEntity } from '../../types'
import { downloadFile } from '../../utils/helpers'
import { getResultFileData } from '../../services/endpoints'
import { NetworkRequestError, RequestErrorResponse } from '../../services/lib'
import { formatError } from '../lib/errors'

const sliceName = 'results'
const DEFAULT_PAGE_SIZE = 50
export const RESULTS_CHUNK_SIZE = 100

interface GetAllResultsOptions {
  desiredPageIndex?: number
  replace?: boolean
}

interface GetAllResultsArgs {
  query?: { [k: string]: string | number }
  meta?: GetAllResultsOptions
}

type PromiseValue<T> = T extends Promise<infer V> ? V : T
type GetAllResultsResponse = PromiseValue<
  ReturnType<typeof Services.getAllResults>
>

export interface ResultState extends EntityState<ResultEntity> {
  loading: boolean
  error: NetworkRequestError | RequestErrorResponse | null
  pageSize: number
  pageIndex: number
  pendingPageIndex: number | null
  total: number
  chunkSize: number
  offset: number
  currentQuery: Record<string, string | number> | null
}

export const getAllResults = createAsyncThunk(
  `${sliceName}/getAllResults`,
  async (args: GetAllResultsArgs | undefined, { rejectWithValue }) => {
    try {
      return await Services.getAllResults(args?.query)
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

export const getResultsSince = createAsyncThunk(
  `${sliceName}/getResultsSince`,
  async (
    query: { [k: string]: string | number } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await Services.getAllResults(query)
      return response.data
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

export const getResult = createAsyncThunk(
  `${sliceName}/getResult`,
  async (id: string, { rejectWithValue }) => {
    try {
      return await Services.getResult(id)
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

export const downloadResultFile = createAsyncThunk(
  `${sliceName}/downloadResultFile`,
  async (
    data: { data: Blob; fileName: string; contentType?: string },
    { rejectWithValue }
  ) => {
    try {
      return await downloadFile(data.data, data.fileName, data.contentType)
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

export const getResultFile = createAsyncThunk(
  `${sliceName}/getResultFileData`,
  async (
    data: {
      resultFileId: string
      query: {
        [k: string]: string
      }
    },
    { rejectWithValue }
  ) => {
    try {
      return await getResultFileData(data.resultFileId, data.query)
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

export const removeResult = createAsyncThunk(
  `${sliceName}/removeResult`,
  async (id: string, { rejectWithValue }) => {
    try {
      return await Services.removeResult(id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

interface UpdateResultActionArgs {
  id: string
  action: Parameters<typeof Services.updateResultAction>[1]
}

export const updateResultAction = createAsyncThunk(
  `${sliceName}/updateResultAction`,
  async ({ id, action }: UpdateResultActionArgs, { rejectWithValue }) => {
    try {
      return await Services.updateResultAction(id, action)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      return rejectWithValue({
        code: 'error',
        message: err.message,
        fields: []
      })
    }
  }
)

export const resultsAdapter = createEntityAdapter<Types.ResultEntity>()

export const initialResultsState: ResultState = resultsAdapter.getInitialState({
  loading: false,
  error: null,
  pageSize: DEFAULT_PAGE_SIZE,
  pageIndex: 0,
  pendingPageIndex: null,
  total: 0,
  chunkSize: RESULTS_CHUNK_SIZE,
  offset: 0,
  currentQuery: null
})

export const resultsSlice = createSlice({
  name: sliceName,
  initialState: initialResultsState,
  reducers: {
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload
    },
    setPendingPageIndex(state, action: PayloadAction<number | null>) {
      state.pendingPageIndex = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllResults.pending, (state, action) => {
        state.loading = true
        state.error = null
        const shouldReplace = action.meta.arg?.meta?.replace ?? true
        const desiredPageIndex = action.meta.arg?.meta?.desiredPageIndex

        if (typeof desiredPageIndex === 'number') {
          state.pendingPageIndex = desiredPageIndex
        }

        if (action.meta.arg?.query) {
          state.currentQuery = action.meta.arg.query
        }

        if (shouldReplace) {
          resultsAdapter.removeAll(state)
        }
      })
      .addCase(getAllResults.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.pendingPageIndex = null

        const { data, pagination } = (action.payload ?? {
          data: [],
          pagination: {
            max: RESULTS_CHUNK_SIZE,
            offset: 0,
            total: 0
          }
        }) as GetAllResultsResponse

        const shouldReplace = action.meta.arg?.meta?.replace ?? true
        const desiredPageIndex = action.meta.arg?.meta?.desiredPageIndex
        const entities = (data ?? []) as unknown as ResultEntity[]

        if (shouldReplace) {
          resultsAdapter.setAll(state, entities)
        } else {
          resultsAdapter.upsertMany(state, entities)
        }

        state.chunkSize = pagination.max
        state.offset = pagination.offset
        state.total = pagination.total
        if (action.meta.arg?.query) {
          state.currentQuery = action.meta.arg.query
        }

        if (typeof desiredPageIndex === 'number') {
          state.pageIndex = desiredPageIndex
        }
      })
      .addCase(getAllResults.rejected, (state, action) => {
        state.loading = false
        state.pendingPageIndex = null
        state.error = action.payload ? formatError(action.payload) : null
        const shouldReplace = action.meta.arg?.meta?.replace ?? true
        if (shouldReplace) {
          resultsAdapter.removeAll(state)
        }
      })
      .addCase(getResultsSince.fulfilled, (state, action) => {
        if (action.payload) {
          resultsAdapter.upsertMany(
            state,
            action.payload as unknown as ResultEntity[]
          )
        }
      })
      .addCase(getResult.fulfilled, (state, action) => {
        if (action.payload) {
          resultsAdapter.upsertOne(
            state,
            action.payload as unknown as ResultEntity
          )
        }
      })
      .addCase(removeResult.fulfilled, (state, action) => {
        const payloadId = action.payload as unknown as string | undefined
        const id = payloadId ?? action.meta.arg
        if (id) {
          resultsAdapter.removeOne(state, id)
        }
      })
      .addCase(removeResult.rejected, (state, action) => {
        state.error = action.payload ? formatError(action.payload) : null
      })
      .addCase(updateResultAction.fulfilled, (state, action) => {
        if (action.payload) {
          resultsAdapter.upsertOne(
            state,
            action.payload as unknown as ResultEntity
          )
        }
      })
  }
})

export const { setPageSize, setPageIndex, setPendingPageIndex } =
  resultsSlice.actions
